import React from "react";
import { Link } from "react-router-dom";
import { getCategory } from "../lib/categories";
import { Volume2, VolumeX } from "lucide-react";

export function CategoryPill({ slug }) {
  const c = getCategory(slug);
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-outfit font-bold uppercase tracking-widest bodh-gradient text-white shadow-sm">
      {c.name_hi}
    </span>
  );
}

export function ReadingMeta({ time, author, dt }) {
  return (
    <div className="flex items-center gap-3 text-xs text-zinc-500 font-hind">
      {author && <span className="font-medium text-zinc-700">{author}</span>}
      {dt && <span>{new Date(dt).toLocaleDateString("hi-IN", { day: "numeric", month: "short" })}</span>}
    </div>
  );
}

/**
 * AutoReadButton — Browser TTS for any article snippet.
 * Stops other utterances on the page when toggled.
 */
export function AutoReadButton({ text, testid, className = "" }) {
  const [speaking, setSpeaking] = React.useState(false);
  React.useEffect(() => () => { try { window.speechSynthesis.cancel(); } catch (e) { /* noop */ } }, []);
  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!("speechSynthesis" in window)) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "hi-IN";
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };
  return (
    <button
      onClick={toggle}
      data-testid={testid || "auto-read-btn"}
      aria-label={speaking ? "Stop reading" : "Read aloud"}
      title={speaking ? "रोकें" : "सुनिए"}
      className={`h-8 w-8 rounded-full flex items-center justify-center transition shadow-md ${
        speaking ? "bodh-gradient text-white" : "bg-white/95 text-zinc-800 hover:bodh-gradient hover:text-white"
      } ${className}`}
    >
      {speaking ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
    </button>
  );
}

export function ArticleCard({ a, size = "md" }) {
  const heightMap = {
    sm: "h-40",
    md: "h-52",
    lg: "h-72",
  };
  const readText = `${a.title}. ${a.subtitle || ""}. ${a.summary || ""}`;
  return (
    <Link to={`/article/${a.slug}`} className="bodh-card group block" data-testid={`article-card-${a.id}`}>
      <div className={`relative ${heightMap[size]} overflow-hidden rounded-2xl bg-zinc-100`}>
        {a.cover_image && (
          <img src={a.cover_image} alt={a.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap max-w-[70%]">
          <CategoryPill slug={a.category} />
          {a.source_name && (
            <span
              data-testid={`article-source-${a.id}`}
              title={`Wire: ${a.source_name}`}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-outfit font-black uppercase tracking-widest bg-white/90 backdrop-blur text-zinc-800 shadow-sm"
            >
              via {a.source_name.split("(")[0].trim().slice(0, 14)}
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-1.5">
          {a.is_breaking && (
            <div className="px-2 py-0.5 rounded-full bg-white text-[10px] font-outfit font-black text-[#E30613] uppercase tracking-widest">
              • Breaking
            </div>
          )}
          <AutoReadButton text={readText} testid={`card-tts-${a.id}`} />
        </div>
        <div className="absolute bottom-0 p-4 pr-12">
          <h3 className="font-devanagari text-white font-bold leading-snug text-base sm:text-lg line-clamp-3 group-hover:text-yellow-200 transition">
            {a.title}
          </h3>
        </div>
      </div>
      <div className="mt-2 px-1">
        <ReadingMeta time={a.reading_time} author={a.author} dt={a.created_at} />
      </div>
    </Link>
  );
}

export function ArticleListItem({ a, index }) {
  return (
    <Link to={`/article/${a.slug}`} className="flex gap-3 items-start group" data-testid={`list-item-${a.id}`}>
      <span className="font-outfit font-black text-3xl leading-none w-8 shrink-0 text-[#E30613]" data-testid={`list-num-${index}`}>{String(index + 1).padStart(2, "0")}</span>
      <div className="min-w-0">
        <h4 className="font-devanagari font-bold text-sm sm:text-[15px] leading-snug text-zinc-900 group-hover:text-[#E30613] transition line-clamp-3">
          {a.title}
        </h4>
        <div className="mt-1.5"><ReadingMeta time={a.reading_time} dt={a.created_at} /></div>
      </div>
    </Link>
  );
}

